import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Cloud, Check, X, Loader2 } from "lucide-react";

const ALL_PROVIDERS = ["aws", "azure", "gcp", "oracle", "cloudflare", "virtualexpanse", "omnissa"];

/**
 * Two modes:
 *   mode="policy-to-clouds": one policy.policy_id, fan out to N providers via /policies/:id/deploy.
 *   mode="policies-to-cloud": single provider, loop /connectors/:provider/push for each policy_id.
 *
 * Props:
 *   open, onOpenChange
 *   mode: "policy-to-clouds" | "policies-to-cloud"
 *   policyIds: string[]
 *   policyName?: string                    (display only, mode="policy-to-clouds")
 *   provider?: string                      (required when mode="policies-to-cloud")
 *   configuredProviders?: string[]         (preselected in mode="policy-to-clouds")
 */
export default function DeployDialog({
  open, onOpenChange,
  mode, policyIds, policyName, provider,
  configuredProviders = [],
}) {
  const [dryRun, setDryRun] = useState(true);
  const [selected, setSelected] = useState([]);
  const [results, setResults] = useState([]);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (!open) return;
    setResults([]);
    setRunning(false);
    setDryRun(true);
    setSelected(configuredProviders.length ? configuredProviders : []);
  }, [open, configuredProviders]);

  const targets = useMemo(() => (
    mode === "policy-to-clouds" ? selected : [provider]
  ), [mode, selected, provider]);

  const canRun = !running && policyIds.length > 0 && targets.length > 0;

  const run = async () => {
    setRunning(true);
    setResults([]);
    try {
      if (mode === "policy-to-clouds") {
        const { data } = await api.post(`/policies/${policyIds[0]}/deploy`, {
          providers: selected, dry_run: dryRun,
        });
        setResults((data?.results || []).map((r) => ({
          ...r, policy_id: policyIds[0],
        })));
      } else {
        // Many policies → one provider. Loop with allSettled.
        const settled = await Promise.allSettled(policyIds.map((pid) =>
          api.post(`/connectors/${provider}/push`, {
            policy_id: pid, dry_run: dryRun,
          }).then((res) => ({ policy_id: pid, ...res.data, ok: true }))
        ));
        setResults(settled.map((s, i) => {
          if (s.status === "fulfilled") return s.value;
          return {
            policy_id: policyIds[i], ok: false,
            error: formatApiError(s.reason?.response?.data?.detail) || s.reason?.message,
          };
        }));
      }
    } finally {
      setRunning(false);
    }
  };

  const toggleProvider = (p) => {
    setSelected((s) => s.includes(p) ? s.filter((x) => x !== p) : [...s, p]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 rounded-sm max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Cloud className="w-4 h-4 text-cyan-400" />
            {mode === "policy-to-clouds"
              ? <>Deploy {policyName ? <span className="text-cyan-300">{policyName}</span> : "policy"} to clouds</>
              : <>Push {policyIds.length} polic{policyIds.length === 1 ? "y" : "ies"} to <span className="text-cyan-300">{provider}</span></>}
          </DialogTitle>
        </DialogHeader>

        {mode === "policy-to-clouds" && (
          <div className="py-2">
            <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">Providers</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {ALL_PROVIDERS.map((p) => {
                const configured = configuredProviders.includes(p);
                const isOn = selected.includes(p);
                return (
                  <button
                    key={p}
                    type="button"
                    data-testid={`deploy-chip-${p}`}
                    onClick={() => toggleProvider(p)}
                    className={`text-[11px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-sm border transition-colors ${
                      isOn
                        ? "bg-cyan-500/20 border-cyan-500/60 text-cyan-300"
                        : configured
                        ? "border-zinc-700 text-zinc-300 hover:border-cyan-500/40"
                        : "border-zinc-800 text-zinc-600 hover:text-zinc-400"
                    }`}
                    title={configured ? "Credentials configured" : "Credentials NOT configured (dry-run only)"}
                  >
                    {p}{!configured && " ·  no creds"}
                  </button>
                );
              })}
            </div>
            {configuredProviders.length === 0 && (
              <p className="text-[10px] font-mono text-amber-400 mt-2">
                No credentials configured anywhere — dry-run is the only safe mode.
              </p>
            )}
          </div>
        )}

        <div className="flex items-center justify-between py-2 border-t border-zinc-800">
          <Label htmlFor="deploy-dry-run" className="text-xs font-mono uppercase tracking-widest text-zinc-300">
            Dry run
          </Label>
          <Switch id="deploy-dry-run" checked={dryRun} onCheckedChange={setDryRun} data-testid="deploy-dry-run-switch" />
        </div>

        {results.length > 0 && (
          <div className="mt-2 border border-zinc-800 rounded-sm overflow-hidden">
            <div className="px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest text-zinc-500 bg-zinc-900/60 border-b border-zinc-800">
              Results
            </div>
            <div className="divide-y divide-zinc-800">
              {results.map((r, i) => (
                <div key={i} className="px-3 py-2 flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2 font-mono">
                    {r.ok ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <X className="w-3.5 h-3.5 text-red-400" />}
                    <span className="text-zinc-200">
                      {r.provider || r.policy_id}
                    </span>
                    {r.dry_run && <span className="text-[9px] uppercase text-amber-400">dry-run</span>}
                  </div>
                  <div className="text-zinc-400 font-mono text-[11px] whitespace-normal break-words max-w-[60%]">
                    {r.ok
                      ? (r.native_id || r.note || (r.applied ? "applied" : "no-op"))
                      : (r.error || "failed")}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter className="mt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
          <Button
            data-testid="deploy-run-btn"
            disabled={!canRun}
            onClick={run}
            className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm gap-2"
          >
            {running && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {dryRun ? "PREVIEW" : "DEPLOY"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
